import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, inject, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/auth.service';
import { VoiceRecorderButtonComponent } from 'src/app/shared/components/recorders/voice-recorder-button/voice-recorder-button.component';
import { TemplatePickerComponent } from 'src/app/shared/components/template-picker/template-picker.component';
import { VoiceNoteCardComponent } from 'src/app/shared/components/ui/voice-note-card/voice-note-card.component';
import { HtmlSplitterHelper } from 'src/app/shared/helpers/html-splitter.helper';
import { HtmlToMarkdownHelper } from 'src/app/shared/helpers/html-to-markdown.helper';
import { ConfirmMenuService } from 'src/app/shared/services/confirm-menu/confirm-menu.service';
import { DynamicOverlayService } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { InsertOptionsMenuComponent } from '../insert-options-menu/insert-options-menu.component';
import { isFileAllowed } from 'src/app/shared/utils/file-validation.utils';
import { ErrorHandlerService } from 'src/app/shared/services/error-handler.service';
import { ChunkService } from '../chunk/chunk.service';
import { ContentBuilderDraft, ContentGeneratorService } from '../content-generator.service';
import { ContentSearcherComponent, ContentSearchResult } from '../content-searcher/content-searcher.component';
import { ContentStateService } from '../content-state.service';

@Component({
  selector: 'app-content-generator',
  templateUrl: './content-generator.component.html',
  styleUrls: ['./content-generator.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatRadioModule,
    MatSelectModule,
    MatTooltipModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    TranslateModule,
    TemplatePickerComponent,
    VoiceRecorderButtonComponent,
    VoiceNoteCardComponent,
  ],
})
export class ContentGeneratorComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private contentGeneratorService = inject(ContentGeneratorService);
  private contentStateService = inject(ContentStateService);
  private chunkService = inject(ChunkService);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);
  private toast = inject(ToastService);
  private confirmMenuService = inject(ConfirmMenuService);
  private dynamicOverlayService = inject(DynamicOverlayService);
  private errorHandler = inject(ErrorHandlerService);

  @Input() courseID: string;
  @Output() close = new EventEmitter<void>();

  private destroy$ = new Subject<void>();

  // Modes: LIST (show drafts), SETUP (ai form), LOADING (generating)
  viewMode = signal<'LIST' | 'SETUP' | 'LOADING'>('LIST');

  aiSetupForm: FormGroup;
  drafts = signal<ContentBuilderDraft[]>([]);
  visibleDrafts = computed(() => this.drafts().filter((d) => d.status !== 'draft'));

  attachedFiles = signal<{ id: string; file?: File; url?: string; filename?: string; type: 'audio' | 'file' | 'video' | 'image'; name: string; uploading?: boolean }[]>([]);
  attachedExternalContexts = signal<ContentSearchResult[]>([]);
  insertingDraftIds = signal<Set<string>>(new Set());

  cannotUseAI = computed(() => !this.authService.canUseAI());

  activeDraftId = signal<string | null>(null);

  interactionId = signal<string | null>(null);

  isRecordingVoice = signal(false);

  dragCounter = 0;
  isDraggingOver = signal(false);

  constructor() {
    const lastTemplate = localStorage.getItem('skz_last_selected_text_gen_template_id') || 'none';
    this.aiSetupForm = this.fb.group({
      subject: ['', Validators.required],
      customization: [''],
      model: ['fast', Validators.required], // fast vs pro
      template: [lastTemplate],
    });
  }

  ngOnInit(): void {
    if (!this.courseID) {
      // Fallback if not provided, try contentStateService
      this.courseID = this.contentStateService.courseId;
    }

    const lectureSubject = this.contentStateService.lecture?.subject;
    if (lectureSubject) {
      this.aiSetupForm.patchValue({ subject: lectureSubject });
    }

    // Auto-save form changes to the active draft
    this.aiSetupForm.valueChanges.pipe(debounceTime(1000), takeUntil(this.destroy$)).subscribe((val) => {
      if (this.activeDraftId()) {
        this.contentGeneratorService
          .updateDraft(this.activeDraftId()!, {
            subject: val.subject,
            customization: val.customization,
            model: val.model,
            templateId: val.template,
          })
          .subscribe({
            next: (updatedDraft) => this.updateLocalDraft(updatedDraft),
            error: (err) => console.error('Failed to auto-save draft', err),
          });
      }
    });

    // Check for pending options (e.g., from "Generate with AI" context menu)
    const pendingOptions = this.contentStateService.consumePendingGeneratorOptions();
    if (pendingOptions) {
      this.viewMode.set('SETUP');
      if (pendingOptions.mediaContext) {
        const fileId = 'initial_' + Date.now();
        this.attachedFiles.update((files) => [
          ...files,
          {
            id: fileId,
            file: pendingOptions.mediaContext!.file,
            url: pendingOptions.mediaContext!.url,
            type: pendingOptions.mediaContext!.type,
            name: pendingOptions.mediaContext!.filename || this.translate.instant('ContentGenerator.AttachedFile'),
            uploading: !!pendingOptions.mediaContext!.file && !pendingOptions.mediaContext!.url,
          },
        ]);
        if (pendingOptions.mediaContext!.file && !pendingOptions.mediaContext!.url) {
          this.uploadFileInternal(pendingOptions.mediaContext!.file, fileId);
        }
      }

      if (pendingOptions.context) {
        if (!this.attachedExternalContexts().find((c) => c.id === pendingOptions.context.id)) {
          this.attachedExternalContexts.update((contexts) => [...contexts, pendingOptions.context]);
        }
      }

      this.aiSetupForm.patchValue({
        subject: pendingOptions.initialSubject || '',
        model: 'fast',
        template: localStorage.getItem('skz_last_selected_text_gen_template_id') || 'none',
      });
      // Automatically create the backing draft for these pending options
      this.contentGeneratorService
        .createDraft({
          contentId: this.courseID || this.contentStateService.courseId,
          subject: pendingOptions.initialSubject || '',
        })
        .subscribe((draft) => {
          if (draft._id) this.activeDraftId.set(draft._id);
          this.saveMediaContexts();
          this.drafts.update((drafts) => [draft, ...drafts]);
        });
    }

    this.contentStateService.openContentGeneratorCommand$.pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
      // If data is an object (not just 'true'), populate the form
      if (typeof data === 'object') {
        this.viewMode.set('SETUP');
        if (data.mediaContext) {
          const fileId = 'initial_' + Date.now();
          this.attachedFiles.update((files) => [
            ...files,
            {
              id: fileId,
              file: data.mediaContext.file,
              url: data.mediaContext.url,
              type: data.mediaContext.type,
              name: data.mediaContext.filename || this.translate.instant('ContentGenerator.AttachedFile'),
              uploading: !!data.mediaContext.file && !data.mediaContext.url,
            },
          ]);
          if (data.mediaContext.file && !data.mediaContext.url) {
            this.uploadFileInternal(data.mediaContext.file, fileId);
          }
        }
        
        if (data.context) {
          if (!this.attachedExternalContexts().find((c) => c.id === data.context.id)) {
            this.attachedExternalContexts.update((contexts) => [...contexts, data.context]);
          }
        }
        
        // We might want to clear previous values or keep them depending on UX.
        // For now, let's reset to defaults but set the subject.
        this.aiSetupForm.patchValue({
          subject: data.initialSubject || '',
          model: 'fast',
          template: localStorage.getItem('skz_last_selected_text_gen_template_id') || 'none',
        });

        this.contentGeneratorService
          .createDraft({
            contentId: this.courseID || this.contentStateService.courseId,
            subject: data.initialSubject || '',
          })
          .subscribe((draft) => {
            if (draft._id) this.activeDraftId.set(draft._id);
            this.saveMediaContexts();
            this.drafts.update((drafts) => [draft, ...drafts]);
          });
      }
    });

    if (this.courseID) {
      this.loadDrafts();
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragEnter(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter++;
    if (this.dragCounter === 1) {
      this.isDraggingOver.set(true);
    }
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter--;
    if (this.dragCounter <= 0) {
      this.dragCounter = 0;
      this.isDraggingOver.set(false);
    }
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter = 0;
    this.isDraggingOver.set(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFiles(files);
    }
  }

  async handleFiles(files: FileList) {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (!isFileAllowed(file)) {
        this.toast.snackbar.open(this.translate.instant('FileUploader.UnsupportedFileType') || 'File type not supported', 'Close', { duration: 3000 });
        continue;
      }

      let mediaType: 'audio' | 'file' | 'video' | 'image' = 'file';
      if (file.type.startsWith('audio/')) mediaType = 'audio';
      else if (file.type.startsWith('video/')) mediaType = 'video';
      else if (file.type.startsWith('image/')) mediaType = 'image';

      const fileId = Date.now().toString() + '_' + i;

      this.attachedFiles.update((filesArray) => [...filesArray, { id: fileId, file, name: file.name, type: mediaType, uploading: true }]);

      this.uploadFileInternal(file, fileId);
    }
  }

  async onFileSelected(event: any) {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    this.handleFiles(files);
    event.target.value = '';
  }

  private async uploadFileInternal(file: File, fileId: string) {
    try {
      const filename = `content_generator_attachments/${this.contentStateService.courseId || this.contentStateService.noteId}/${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
      const snapshot: any = await this.authService.uploadFile(file, filename, undefined, true);
      const gsUrl = `gs://${snapshot.ref.bucket}/${snapshot.ref.fullPath}`;

      const mimeType = file.type.split(';')[0];

      this.attachedFiles.update((filesArray) => filesArray.map((f) => (f.id === fileId ? { ...f, url: gsUrl, filename, uploading: false } : f)));
      this.saveMediaContexts();
    } catch (err) {
      console.error('File upload failed', err);
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error') || 'Upload failed', 'Close', { duration: 3000 });
      this.removeAttachment(fileId);
    }
  }

  removeAttachment(fileId: string) {
    const fileToRemove = this.attachedFiles().find((f) => f.id === fileId);
    if (fileToRemove && fileToRemove.filename) {
      this.authService.deleteFile(fileToRemove.filename).catch((err) => console.error('Failed to delete file from storage', err));
    }
    this.attachedFiles.update((filesArray) => filesArray.filter((f) => f.id !== fileId));
    this.saveMediaContexts();
  }

  removeExternalContext(id: string) {
    this.attachedExternalContexts.update((contexts) => contexts.filter((c) => c.id !== id));
    this.saveMediaContexts();
  }

  private cdkOverlay = inject(Overlay);
  private searcherOverlayRef: OverlayRef | null = null;

  toggleContentSearcher() {
    if (this.searcherOverlayRef) {
      this.searcherOverlayRef.dispose();
      this.searcherOverlayRef = null;
      return;
    }

    const positionStrategy = this.cdkOverlay.position().global().centerHorizontally().centerVertically();

    this.searcherOverlayRef = this.cdkOverlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-dark-backdrop',
    });

    const portal = new ComponentPortal(ContentSearcherComponent);
    const compRef = this.searcherOverlayRef.attach(portal);

    compRef.instance.itemSelected.subscribe((result: ContentSearchResult) => this.onContentSearcherSelect(result));
    compRef.instance.closed.subscribe(() => {
      this.searcherOverlayRef?.dispose();
      this.searcherOverlayRef = null;
    });

    this.searcherOverlayRef.backdropClick().subscribe(() => {
      this.searcherOverlayRef?.dispose();
      this.searcherOverlayRef = null;
    });
  }

  async onContentSearcherSelect(result: ContentSearchResult) {
    if (!result?.id) return;
    if (this.attachedExternalContexts().find((c) => c.id === result.id)) return;

    this.attachedExternalContexts.update((contexts) => [...contexts, result]);
  }

  loadDrafts() {
    this.contentGeneratorService
      .getDrafts(this.courseID, 'text')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (drafts) => this.drafts.set(drafts || []),
        error: (err) => console.error('Failed to load drafts', err),
      });
  }

  openAiSetup() {
    if (this.cannotUseAI()) return;
    const existingSetupDraft = this.drafts().find((d) => d.status === 'draft');
    if (existingSetupDraft) {
      this.openDraft(existingSetupDraft);
      return;
    }

    this.viewMode.set('SETUP');
    const contentId = this.courseID || this.contentStateService.courseId;
    if (contentId) {
      this.contentGeneratorService.createDraft({ contentId }).subscribe({
        next: (draft) => {
          if (draft._id) {
            this.activeDraftId.set(draft._id);
            this.drafts.update((drafts) => [draft, ...drafts]);
          }
        },
        error: (err) => console.error('Failed to create setup draft', err),
      });
    }
  }

  saveMediaContexts() {
    if (!this.activeDraftId()) return;

    const readyAttachments = this.attachedFiles().filter((a) => !a.uploading && a.url);
    const mediaContent = readyAttachments.map((a) => ({ url: a.url!, type: a.type, filename: a.name }));

    this.contentGeneratorService
      .updateDraft(this.activeDraftId()!, {
        mediaContent,
      })
      .subscribe({
        next: (updatedDraft) => this.updateLocalDraft(updatedDraft),
        error: (err) => console.error('Failed to auto-save media context', err),
      });
  }

  private updateLocalDraft(updatedDraft: ContentBuilderDraft) {
    this.drafts.update((drafts) => drafts.map((d) => (d._id === updatedDraft._id ? updatedDraft : d)));
  }

  goBackToList() {
    this.viewMode.set('LIST');
  }

  cancelSetup() {
    this.attachedFiles().forEach((f) => {
      if (f.filename) {
        this.authService.deleteFile(f.filename).catch((err) => console.error('Failed to delete file from storage', err));
      }
    });
    this.viewMode.set('LIST');
    this.aiSetupForm.reset({
      model: 'fast',
      template: localStorage.getItem('skz_last_selected_text_gen_template_id') || 'none',
    });
    this.attachedFiles.set([]);
    this.attachedExternalContexts.set([]);
    this.interactionId.set(null);
    this.activeDraftId.set(null);
  }

  openDraft(draft: ContentBuilderDraft) {
    if (draft.status === 'draft') {
      // Resume setup phase
      if (draft._id) this.activeDraftId.set(draft._id);
      const lastSelected = localStorage.getItem('skz_last_selected_text_gen_template_id') || 'none';
      const templateValue = (draft.templateId !== undefined && draft.templateId !== null) ? draft.templateId : lastSelected;
      this.aiSetupForm.patchValue(
        {
          subject: draft.subject || '',
          customization: draft.customization || '',
          model: draft.model || 'fast',
          template: templateValue,
        },
        { emitEvent: false }
      ); // don't trigger autosave immediately

      if (draft.mediaContent && draft.mediaContent.length > 0) {
        const files = draft.mediaContent.map((m: any, i: number) => ({
          id: 'restored_' + i,
          url: m.url,
          filename: m.filename,
          type: m.type,
          name: m.filename || m.url.split('/').pop() || 'Attached File',
          uploading: false,
        }));
        this.attachedFiles.set(files);
      } else {
        this.attachedFiles.set([]);
      }
      this.viewMode.set('SETUP');
      return;
    }

    // This logic relies on modifying ContentDisplayCard to handle 'source' and 'draftId'
    // Alternatively, we can use ContentStateService to signal the main displayer

    // For now, let's assume we call a method on ContentStateService to open the editor
    // We haven't implemented openCourseDraft in ContentStateService yet, but per plan:
    // "Update ContentStateService to manage course builder state"
    // "ContentDisplayCardComponent ... Render <app-canvas-editor> when opening a builder draft"

    // We will assume ContentStateService has an `openCourseDraft(draft)` method or similar
    // For now, let's emit an event or update state locally if this component is the manager.
    // Given the architecture, this component is likely inside ContentDisplayCard.

    // Let's implement the logic to call a new method in ContentStateService
    this.contentStateService.openContentGeneratorDraft(draft);
    // Automatically open the refiner sidebar to show versions
    setTimeout(() => {
      this.contentStateService.openContentRefiner(draft);
    }, 100);
  }
  allFilesDoneUploading() {
    return this.attachedFiles().every((a) => !a.uploading);
  }
  generateContent() {
    if (this.aiSetupForm.invalid) {
      this.aiSetupForm.markAllAsTouched();
      return;
    }

    const { subject, customization, model, template } = this.aiSetupForm.getRawValue();
    this.viewMode.set('LOADING');

    const readyAttachments = this.attachedFiles().filter((a) => !a.uploading && a.url);
    this.contentGeneratorService
      .generateContent({
        draftId: this.activeDraftId() || undefined,
        contentId: this.courseID,
        subject,
        customization,
        model,
        templateId: template,
        interactionId: this.interactionId() || undefined,
        mediaContexts: readyAttachments.length > 0 ? readyAttachments.map((a) => ({ url: a.url!, type: a.type, filename: a.name })) : undefined,
        attachedContentIds: this.attachedExternalContexts().filter((c) => c.type !== 'chunk').map((c) => c.id),
        attachedChunkIds: this.attachedExternalContexts().filter((c) => c.type === 'chunk').map((c) => c.id),
      })
      .subscribe({
        next: (draft) => {
          this.loadDrafts(); // Refresh list to show new draft
          this.viewMode.set('LIST');
          this.interactionId.set(null);
          this.activeDraftId.set(null);
          this.attachedFiles.set([]);
          this.attachedExternalContexts.set([]);
          // Optionally open the draft immediately
          this.openDraft(draft);
        },
        error: (err) => {
          console.error('Generation failed', err);
          this.errorHandler.handleAiRestrictedError(err);
          this.viewMode.set('SETUP');
        },
      });
  }

  deleteDraft(draftId: string, event: Event, trigger: MatIconButton) {
    event.stopPropagation();
    this.confirmMenuService.confirm(trigger._elementRef, 'Global.AreYouSure', 'delete_outline').subscribe(() => {
      this.contentGeneratorService.deleteDraft(draftId).subscribe(() => {
        this.loadDrafts();
      });
    });
  }

  confirmInsertDraft(event: Event, draft: ContentBuilderDraft, trigger: MatIconButton) {
    event.stopPropagation();
    const { ref: overlayRef, componentInstance } = this.dynamicOverlayService.open(
      trigger._elementRef,
      InsertOptionsMenuComponent
    );

    const sub = componentInstance.optionSelected.subscribe((option: 'chunks' | 'canvas' | 'markdown') => {
      this.dynamicOverlayService.close();
      this.insertDraft(draft, option);
    });

    overlayRef.detachments().subscribe(() => {
      sub.unsubscribe();
    });
  }

  async insertDraft(draft: ContentBuilderDraft, insertMode: 'chunks' | 'canvas' | 'markdown' = 'chunks') {
    if (this.insertingDraftIds().has(draft._id!)) return;

    this.insertingDraftIds.update((s) => {
      const newSet = new Set(s);
      newSet.add(draft._id!);
      return newSet;
    });

    // Use current version content or draft content
    const content = draft.versions?.find((v) => v._id === draft.currentVersionId)?.content || draft.content || '';

    if (!content.trim()) {
      this.toast.snackbar.open('No content to insert', 'Ok', { duration: 2000 });
      this.insertingDraftIds.update((s) => {
        const newSet = new Set(s);
        newSet.delete(draft._id!);
        return newSet;
      });
      return;
    }

    try {
      const userData = this.authService.userInfo();
      const safeUser = userData || JSON.parse(localStorage.getItem('userData') || '{}');

      let chunkPayloads: any[];

      if (insertMode === 'chunks') {
        const chunks = HtmlSplitterHelper.split(content);
        if (chunks.length === 0) {
          this.toast.snackbar.open('No content to insert', 'Ok', { duration: 2000 });
          this.insertingDraftIds.update((s) => {
            const newSet = new Set(s);
            newSet.delete(draft._id!);
            return newSet;
          });
          return;
        }

        chunkPayloads = chunks.map((chunkHtml) => ({
          _id: Date.now().toString() + Math.random().toString().slice(2, 8),
          content: chunkHtml,
          type: 'html',
          language: this.translate.currentLang,
          status: 'creating',
          params: {},
          editions: [],
          user: {
            id: safeUser?.id,
            fullName: `${safeUser?.firstName} ${safeUser?.lastName}`,
            firstName: safeUser?.firstName,
            lastName: safeUser?.lastName,
            email: safeUser?.email,
          },
          courseID: this.contentStateService.courseId,
          lecture: this.contentStateService.lecture,
          section: this.contentStateService.section,
          blogID: this.contentStateService.blogId,
          noteID: this.contentStateService.noteId,
          contentID: this.contentStateService.contentId,
          contentName: draft.title || 'Generated Content',
        }));
      } else if (insertMode === 'markdown') {
        chunkPayloads = [{
          _id: Date.now().toString() + Math.random().toString().slice(2, 8),
          content: HtmlToMarkdownHelper.convert(content),
          type: 'code',
          codeLanguage: 'markdown',
          language: this.translate.currentLang,
          status: 'creating',
          params: {},
          editions: [],
          user: {
            id: safeUser?.id,
            fullName: `${safeUser?.firstName} ${safeUser?.lastName}`,
            firstName: safeUser?.firstName,
            lastName: safeUser?.lastName,
            email: safeUser?.email,
          },
          courseID: this.contentStateService.courseId,
          lecture: this.contentStateService.lecture,
          section: this.contentStateService.section,
          blogID: this.contentStateService.blogId,
          noteID: this.contentStateService.noteId,
          contentID: this.contentStateService.contentId,
          title: draft.title || 'Markdown Content',
          contentName: draft.title || 'Generated Content',
        }];
      } else {
        chunkPayloads = [{
          _id: Date.now().toString() + Math.random().toString().slice(2, 8),
          content: content,
          type: 'canvas-transcript',
          language: this.translate.currentLang,
          status: 'creating',
          params: {},
          editions: [],
          user: {
            id: safeUser?.id,
            fullName: `${safeUser?.firstName} ${safeUser?.lastName}`,
            firstName: safeUser?.firstName,
            lastName: safeUser?.lastName,
            email: safeUser?.email,
          },
          courseID: this.contentStateService.courseId,
          lecture: this.contentStateService.lecture,
          section: this.contentStateService.section,
          blogID: this.contentStateService.blogId,
          noteID: this.contentStateService.noteId,
          contentID: this.contentStateService.contentId,
          title: draft.title || 'Transcription',
          contentName: draft.title || 'Generated Content',
        }];
      }

      await firstValueFrom(
        this.chunkService.createChunk({
          chunks: chunkPayloads,
          insertBelowID: undefined,
        })
      );

      this.toast.snackbar.open(this.translate.instant('Global.Saved'), 'Ok', { duration: 2000 });
      // Optionally close or just finish loading
    } catch (e) {
      console.error('Failed to insert content', e);
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
    } finally {
      this.insertingDraftIds.update((s) => {
        const newSet = new Set(s);
        newSet.delete(draft._id!);
        return newSet;
      });
    }
  }

  onVoiceRecorded(file: File) {
    const id = Date.now().toString();
    this.attachedFiles.update((files) => [...files, { id, file, name: file.name, type: 'audio', uploading: true }]);
    this.uploadFileInternal(file, id);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
